/**
 * Reactions under a message, post, comment or card (t295u): plain emoji and count with no pill outline, your own marked
 * with a faint tint and a semibold count. Hover or focus shows "Sarah K, Tom B and 3 others reacted with 👍"; a click
 * (tap) toggles yours. A muted "4 people" at the end, a long-press, a right-click or Alt+Enter / ArrowDown on a reaction
 * opens who reacted: a popover on a computer, the Reactions sheet on the phone, with All and a tab per emoji; a name
 * opens the person. Names come from one batched request per screen and refresh with the realtime reaction list.
 */
import { useId, useRef, useState } from 'react';
import clsx from 'clsx';
import { reactorsSentence, type Reactor } from '@tradeoutlet/shared';
import { useAuth } from '@/lib/auth';
import { useMobileLayout } from '@/mobile/layout';
import { swallowNextClick } from '@/mobile/longPress';
import type { ReactorKind } from './batcher';
import { useReactors, type PillData } from './useReactors';
import { ALL, ReactionsPopover, ReactionsSheet, reactorCount } from './ReactorsList';

export { useReactors, type PillData };

/** How many different people reacted: "1 person", "4 people". */
export const peopleLabel = (n: number) => `${n} ${n === 1 ? 'person' : 'people'}`;

export function ReactionBar({ kind, id, pills, onToggle, size = 'sm', className, showEmpty, onShowReactors, summary = true }: {
  kind: ReactorKind; id: string; pills: PillData[]; onToggle: (emoji: string) => void; size?: 'sm' | 'md' | 'lg'; className?: string; showEmpty?: boolean;
  /** t255u: who reacted is shown by the caller (the item's sheet or popover) instead of the bar's own. */ onShowReactors?: (emoji: string) => void;
  /** t295u: the muted "4 people" link at the end of the row (on by default; shown from two people). */ summary?: boolean;
}) {
  const { userId } = useAuth();
  const phone = useMobileLayout();
  const q = useReactors(kind, id, pills);
  const [tip, setTip] = useState<string | null>(null);
  const [open, setOpen] = useState<string | null>(null);
  const press = useRef<ReturnType<typeof setTimeout>>();
  const longPressed = useRef(false);
  const tipId = useId();
  const list = (emoji: string): Reactor[] => q.data?.[emoji] ?? [];
  const sentence = (p: PillData) => (q.data ? reactorsSentence(list(p.emoji), p.emoji, userId) : p.count ? `${p.count} reacted with ${p.emoji}` : `React with ${p.emoji}`);
  const withPeople = pills.filter(p => p.count > 0);
  const people = reactorCount(withPeople);
  const show = (emoji: string) => { setTip(null); if (onShowReactors) onShowReactors(emoji); else setOpen(emoji); };
  const big = size === 'lg';
  return (
    <div className={clsx('relative flex flex-wrap items-center gap-0.5', className)} data-testid="reaction-bar">
      {pills.filter(p => showEmpty || p.count > 0).map(p => {
        const mine = p.user_ids.includes(userId ?? '');
        return (
          <span key={p.emoji} className="relative">
            <button type="button" aria-pressed={mine} data-testid="reaction-pill" data-mine={mine || undefined} aria-describedby={tip === p.emoji ? tipId : undefined}
              aria-label={`${p.emoji} ${p.count}. ${p.count ? sentence(p) : ''}${p.count ? '. Alt+Enter to see everyone' : ''}`}
              onMouseEnter={() => setTip(p.emoji)} onMouseLeave={() => setTip(t => (t === p.emoji ? null : t))}
              onFocus={() => setTip(p.emoji)} onBlur={() => setTip(t => (t === p.emoji ? null : t))}
              onPointerDown={e => { longPressed.current = false; const touch = e.pointerType === 'touch'; if (p.count) press.current = setTimeout(() => { longPressed.current = true; if (touch) swallowNextClick(); show(p.emoji); }, 500); }}
              onPointerUp={() => clearTimeout(press.current)} onPointerLeave={() => clearTimeout(press.current)}
              // t255u: a reaction's long-press is its own (never the card's react sheet around it).
              onTouchStart={e => e.stopPropagation()}
              onContextMenu={e => { e.stopPropagation(); if (p.count) { e.preventDefault(); clearTimeout(press.current); if (!longPressed.current) { longPressed.current = true; show(p.emoji); } } }}
              onKeyDown={e => { if (p.count && ((e.key === 'Enter' && e.altKey) || e.key === 'ArrowDown')) { e.preventDefault(); show(p.emoji); } }}
              onClick={() => { if (longPressed.current) { longPressed.current = false; return; } onToggle(p.emoji); }}
              // t295u: no outline; yours has a faint tint and a semibold count; a light background on hover.
              className={clsx('flex select-none items-center gap-1 rounded-md text-slate-700 dark:text-slate-200',
                big ? 'h-9 min-w-9 justify-center px-2 text-base' : size === 'md' ? 'h-7 px-1.5 text-sm' : phone ? 'h-8 min-w-8 justify-center px-1.5 text-sm' : 'h-6 px-1.5 text-sm',
                mine ? 'bg-reaction-mine text-on-reaction-mine hover:bg-reaction-mine-hover' : 'hover:bg-slate-100 active:bg-slate-100 dark:hover:bg-slate-800 dark:active:bg-slate-800',
                !p.count && 'opacity-60')}>
              <span aria-hidden>{p.emoji}</span>{p.count > 0 && <span className={clsx('tabular-nums', big ? 'text-sm' : 'text-xs', mine ? 'font-semibold' : 'font-medium')}>{p.count}</span>}
            </button>
            {tip === p.emoji && p.count > 0 && !open && (
              <span role="tooltip" id={tipId} data-testid="reaction-tooltip"
                className="pointer-events-none absolute bottom-full left-1/2 z-50 mb-1.5 w-max max-w-64 -translate-x-1/2 rounded-md bg-slate-900 px-2 py-1 text-center text-xs text-white shadow-lg">
                {sentence(p)}
              </span>
            )}
          </span>
        );
      })}
      {/* One reactor is named by the hover tip or a long-press; "1 person" under every message was noise. */}
      {summary && people > 1 && (
        <button type="button" onClick={e => { e.stopPropagation(); show(ALL); }} aria-haspopup="dialog" data-testid="reactions-summary"
          aria-label={`${peopleLabel(people)} reacted, see who`}
          className={clsx('ml-1 shrink-0 rounded px-1 text-slate-500 hover:text-slate-800 hover:underline focus-visible:outline-2 dark:text-slate-400 dark:hover:text-slate-200',
            big ? 'h-9 text-sm' : phone ? 'h-8 text-xs' : 'h-6 text-xs')}>
          {peopleLabel(people)}
        </button>
      )}
      {open && (phone || big
        ? <ReactionsSheet kind={kind} id={id} pills={withPeople} initial={open} onClose={() => setOpen(null)} />
        : <ReactionsPopover kind={kind} id={id} pills={withPeople} initial={open} onClose={() => setOpen(null)} />)}
    </div>
  );
}
